import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Section, Eyebrow } from "@/components/PublicLayout";
import { useI18n } from "@/i18n";
import { ProgramFinder, C as HOME_COPY } from "@/pages/Home";
import { IMG } from "@/lib/images";
import { GraduationCap, Globe2, Plane, Briefcase, Award, ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CAMPUS = "https://images.unsplash.com/photo-1576495199011-eb94736d05d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const WA = "https://wa.me/14154297403";

const T = {
  en: {
    eyebrow: "An international university · welcoming Africa's finest",
    h1a: "A globally recognised degree.", h1b: "World-class faculty.", h1c: "Begin from home.",
    lead: "Simo University is an international, Western-modelled institution with component schools from nursery to PhD. Study online first — then continue on-site at a Simo or partner campus as your journey and readiness allow.",
    ctaApply: "Begin your application", ctaProg: "Find your programme", ctaScholar: "African & Asian scholarships",
    pillars: "Built for ambitious African families",
    supportTitle: "We walk the full journey with you",
    disclaimer: "We support — we do not guarantee. We provide structured assistance with admissions, mobility and graduate pathways, but do not promise specific salaries, placement rates, employer hiring or visa outcomes. Any labour-market figures we publish carry a source, date and geographic context in our Claims Registry. Tuition follows the Africa scale for students residing and studying from Africa.",
    k12t: "From nursery to Grade 12", k12b: "The same rigorous, mastery-based Simo education offered worldwide — with tuition adapted to the Africa scale and dedicated scholarships, so readiness (not budget) sets the pace.",
    k12cta: "Explore K–12",
    scholarTitle: "Dedicated scholarships for African & Asian students",
    scholarBody: "The Founding Cohort Grant and regional scholarships reduce tuition for eligible families across Africa and Asia. Fees are shown in your local reality and payable by Mobile Money or card.",
    pathTitle: "Online first. On-site when you're ready.",
    pathBody: "Start rigorous, globally benchmarked study from your city. As you progress, unlock an optional on-site component at a Simo campus or a partner-school campus abroad.",
  },
  fr: {
    eyebrow: "Une université internationale · qui accueille les meilleurs talents d'Afrique",
    h1a: "Un diplôme reconnu mondialement.", h1b: "Un corps professoral d'excellence.", h1c: "Commencez depuis chez vous.",
    lead: "Simo University est une institution internationale de modèle occidental, avec des écoles composantes de la maternelle au doctorat. Étudiez d'abord en ligne — puis poursuivez sur un campus Simo ou partenaire, selon votre parcours et votre préparation.",
    ctaApply: "Commencer une candidature", ctaProg: "Trouver mon programme", ctaScholar: "Bourses Afrique & Asie",
    pillars: "Conçu pour les familles africaines ambitieuses",
    supportTitle: "Nous vous accompagnons de bout en bout",
    disclaimer: "Nous accompagnons — nous ne garantissons pas. Nous offrons une assistance structurée pour l'admission, la mobilité et l'insertion, sans promettre de salaires, de taux de placement, d'embauche ou de résultats de visa. Toute donnée du marché du travail publiée porte une source, une date et un contexte géographique dans notre Registre des Affirmations. Les frais suivent le barème Afrique pour les étudiants résidant et étudiant depuis l'Afrique.",
    k12t: "De la maternelle à la Terminale", k12b: "La même éducation Simo, rigoureuse et fondée sur la maîtrise, proposée dans le monde entier — avec des frais adaptés au barème Afrique et des bourses dédiées, pour que la préparation (et non le budget) donne le rythme.",
    k12cta: "Découvrir le K–12",
    scholarTitle: "Bourses dédiées aux étudiants africains & asiatiques",
    scholarBody: "La Bourse de la Cohorte Fondatrice et les bourses régionales réduisent les frais pour les familles éligibles en Afrique et en Asie. Les frais sont affichés selon votre réalité locale et payables par Mobile Money ou carte.",
    pathTitle: "En ligne d'abord. Sur place quand vous êtes prêt.",
    pathBody: "Commencez des études rigoureuses et alignées sur les standards mondiaux depuis votre ville. En progressant, débloquez une composante optionnelle sur un campus Simo ou un campus partenaire à l'étranger.",
  },
};

export default function Africa() {
  const { lang } = useI18n();
  const t = T[lang === "fr" ? "fr" : "en"];
  const finderCopy = HOME_COPY[lang] || HOME_COPY.en;
  const [hero, setHero] = useState(null);
  const [progs, setProgs] = useState([]);
  useEffect(() => {
    api.get("/content/settings").then((r) => setHero(r.data.home_hero)).catch(() => {});
    api.get("/programmes").then((r) => setProgs(r.data)).catch(() => {});
  }, []);
  const h = hero || { media_type: "image", image_url: IMG.africa, overlay: 58 };
  const PILLARS = [
    { icon: Award, t: lang === "fr" ? "Bourses & ROI" : "Scholarships & ROI", b: lang === "fr" ? "Frais adaptés + bourses Afrique/Asie, payables par Mobile Money." : "Adapted tuition + Africa/Asia scholarships, payable by Mobile Money." },
    { icon: Globe2, t: lang === "fr" ? "Reconnaissance internationale" : "International standing", b: lang === "fr" ? "Un cursus de modèle occidental, en anglais et en français." : "A Western-modelled curriculum, taught in English and French." },
    { icon: Plane, t: lang === "fr" ? "Mobilité & visa" : "Mobility & visa", b: lang === "fr" ? "Assistance à la mobilité et aux démarches de visa étudiant." : "Structured assistance with mobility and student-visa steps." },
    { icon: Briefcase, t: lang === "fr" ? "Insertion après diplôme" : "Graduate pathways", b: lang === "fr" ? "Préparation à l'emploi et accompagnement au placement." : "Career preparation and graduate placement assistance." },
    { icon: GraduationCap, t: lang === "fr" ? "Maternelle → Doctorat" : "Nursery → PhD", b: lang === "fr" ? "Des écoles composantes pour chaque étape de la vie." : "Component schools for every stage of life." },
    { icon: ShieldCheck, t: lang === "fr" ? "Progression par la maîtrise" : "Mastery-based", b: lang === "fr" ? "La préparation détermine la progression, pas l'âge." : "Readiness determines progression, not age." },
  ];
  return (
    <div data-testid="africa-page">
      {/* HERO — same full-bleed video/finder hero as the global home, Africa-adapted */}
      <div className="relative overflow-hidden bg-ink text-paper min-h-screen flex items-center">
        {h.media_type === "youtube" && h.video_url && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <iframe title="hero" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] h-[100vh] min-w-full min-h-full"
              src={`https://www.youtube.com/embed/${h.video_url}?autoplay=1&mute=1&loop=1&playlist=${h.video_url}&controls=0&showinfo=0&modestbranding=1&rel=0&playsinline=1&iv_load_policy=3`}
              allow="autoplay; encrypted-media" frameBorder="0" />
          </div>
        )}
        {h.media_type === "video" && h.video_url && (
          <video className="absolute inset-0 h-full w-full object-cover" src={h.video_url} autoPlay muted loop playsInline />
        )}
        {h.media_type === "image" && (h.image_url || IMG.africa) && (
          <img src={h.image_url || IMG.africa} alt="" className="absolute inset-0 h-full w-full object-cover object-[70%_center]" />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(10,11,15,${Math.min(0.55, (h.overlay ?? 55) / 110)}) 0%, rgba(10,11,15,0.45) 55%, rgba(10,11,15,0.6) 100%)` }} />
        <Section className="relative w-full pt-24">
          <div className="max-w-3xl [text-shadow:0_2px_28px_rgba(0,0,0,0.6)]">
            <Eyebrow>Simo University · {t.eyebrow}</Eyebrow>
            <h1 className="font-serif text-[3rem] leading-[1.02] md:text-7xl md:leading-[0.98] tracking-tight font-light" data-testid="hero-title">
              {t.h1a} {t.h1b} <span className="text-heraldic">{t.h1c}</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg md:text-xl text-paper/80 font-light" data-testid="hero-sub">{t.lead}</p>
            <div className="mt-9 max-w-2xl"><ProgramFinder progs={progs} copy={finderCopy} /></div>
            <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-paper/70">
              <Link to="/apply" data-testid="africa-apply" className="group inline-flex items-center gap-2 text-white hover:text-heraldic transition-colors">{t.ctaApply} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></Link>
              <Link to="/scholarships" data-testid="africa-scholarships" className="hover:text-white transition-colors">{t.ctaScholar}</Link>
              <a href={WA} target="_blank" rel="noreferrer" data-testid="africa-whatsapp" className="inline-flex items-center gap-2 hover:text-white transition-colors"><MessageCircle className="h-4 w-4 text-emerald-400" /> WhatsApp</a>
            </div>
          </div>
        </Section>
      </div>

      <Section className="py-16 md:py-24">
        <Eyebrow>{t.pillars}</Eyebrow>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map((p) => (
            <div key={p.t} className="border border-border rounded-sm p-6 hover:border-heraldic/50 transition-colors">
              <p.icon className="h-6 w-6 text-heraldic" />
              <h3 className="mt-4 font-serif text-xl text-ink">{p.t}</h3>
              <p className="mt-2 text-ink/60 text-sm leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <div className="bg-ink text-paper">
        <Section className="py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Eyebrow>{t.scholarTitle}</Eyebrow>
            <p className="mt-5 text-paper/75 leading-relaxed">{t.scholarBody}</p>
            <Link to="/scholarships" data-testid="africa-scholarships" className="mt-7 inline-flex items-center gap-2 bg-heraldic text-white px-6 py-3 rounded-sm hover:bg-heraldic-600 transition-colors">{t.ctaScholar} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="rounded-md overflow-hidden">
            <img src={CAMPUS} alt="" loading="lazy" decoding="async" className="w-full h-64 md:h-80 object-cover" />
          </div>
        </Section>
      </div>

      <Section className="py-16 md:py-24 grid md:grid-cols-2 gap-12">
        <div className="border-l-2 border-heraldic pl-6">
          <h3 className="font-serif text-2xl text-ink">{t.pathTitle}</h3>
          <p className="mt-3 text-ink/65 leading-relaxed">{t.pathBody}</p>
        </div>
        <div className="border-l-2 border-heraldic pl-6">
          <h3 className="font-serif text-2xl text-ink">{t.k12t}</h3>
          <p className="mt-3 text-ink/65 leading-relaxed">{t.k12b}</p>
          <Link to="/schools" data-testid="africa-k12" className="mt-4 inline-flex items-center gap-1.5 text-heraldic">{t.k12cta} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </Section>

      <Section className="pb-20">
        <div className="border border-border rounded-sm p-6 max-w-4xl">
          <p className="text-ink/55 text-sm leading-relaxed">{t.disclaimer}</p>
        </div>
      </Section>
    </div>
  );
}
